// Shared location resolution: turn free text (ZIP, city, county, state) into a place
// the site can act on. Built on the USPS 3-digit prefix table in ./zip-prefixes.
//
// Two entry points, deliberately split so the 362KB county dataset never has to load
// just to answer "what state am I in?":
//   * resolveQuery()   — state-level only. Zero heavy imports. Used by the homepage
//                        checker and by the map before county data is available.
//   * searchLocations()— ranked suggestions, including counties, but only when the
//                        caller passes a county list in (the map does, after it has
//                        dynamically imported the data it needs anyway).
import { STATES, STATE_ABBR_TO_NAME, getStateByAbbr, type StateInfo } from "./states";
import { countyLabel } from "./county-label";
import {
  looksLikeZip,
  resolveLocation,
  uncoveredZipPlace,
  type ResolvedLocation,
} from "./zip-prefixes";

export type { ResolvedLocation };
export { resolveLocation, looksLikeZip, uncoveredZipPlace };

export type LocationMethod = "zip" | "city" | "state" | "county";

export interface CountyRef {
  fips: string;
  name: string;
  slug: string;
}

export interface LocationMatch {
  /** How the query was resolved — drives the "state-level only" disclaimer copy. */
  method: LocationMethod;
  /** Human label for the match, e.g. "Travis County, TX" or "Georgia". */
  label: string;
  stateAbbr: string;
  stateName: string;
  /** The state's awareness entry, when we publish a page for it. */
  state?: StateInfo;
  /** Present only for county matches. */
  county?: CountyRef;
  /** The raw text the user typed. */
  query: string;
}

function stateMatch(
  abbr: string,
  method: LocationMethod,
  query: string,
  label?: string,
): LocationMatch | null {
  const stateName = STATE_ABBR_TO_NAME[abbr];
  if (!stateName) return null;
  return {
    method,
    label: label ?? stateName,
    stateAbbr: abbr,
    stateName,
    state: getStateByAbbr(abbr),
    query,
  };
}

/**
 * Resolve a query to a state without touching county data. Returns null when nothing
 * matches — callers show the "we couldn't match that" branch rather than guessing.
 */
export function resolveQuery(query: string): LocationMatch | null {
  const resolved: ResolvedLocation | null = resolveLocation(query);
  if (!resolved) return null;
  return stateMatch(
    resolved.abbr,
    resolved.method,
    resolved.query,
    resolved.place ? `${resolved.place}, ${resolved.abbr}` : undefined,
  );
}

/** Normalize for comparison: lowercase, strip punctuation and the "county"/"parish" noise. */
export function normalizePlace(s: string): string {
  return s
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\b(county|parish|borough|census area|city and borough|municipality)\b/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/**
 * The minimum a county has to look like to be searchable. Structural on purpose: the
 * full dataset in ./counties satisfies it, and so does the geometry layer's much
 * smaller county index — so the map can search counties without loading either the
 * 362KB dataset or any geometry.
 */
export interface SearchableCounty {
  fips: string;
  name: string;
  stateAbbr: string;
  slug: string;
}

export interface SearchOptions {
  /** County list to search. Omit to search states only (keeps the bundle light). */
  counties?: readonly SearchableCounty[];
  /** Max results. Default 8. */
  limit?: number;
}

/**
 * Ranked location suggestions for a search box. Exact matches sort above prefix
 * matches, which sort above substring matches; states outrank counties at equal rank
 * so typing "Texas" offers the state first.
 */
export function searchLocations(query: string, options: SearchOptions = {}): LocationMatch[] {
  const limit = options.limit ?? 8;
  const raw = query.trim();
  if (!raw) return [];

  // A ZIP is unambiguous — answer it directly and stop. Street addresses are not
  // ZIPs, however much they start with a digit.
  if (looksLikeZip(raw)) {
    const direct = resolveQuery(raw);
    return direct ? [direct] : [];
  }

  const q = normalizePlace(raw);
  if (!q) return [];

  const scored: Array<{ score: number; match: LocationMatch }> = [];

  const rank = (name: string): number => {
    const n = normalizePlace(name);
    if (n === q) return 0;
    if (n.startsWith(q)) return 1;
    if (n.includes(q)) return 2;
    return -1;
  };

  for (const s of STATES) {
    // Abbreviation typed on its own ("TX") is an exact hit.
    const score = s.abbr.toLowerCase() === q ? 0 : rank(s.name);
    if (score < 0) continue;
    const m = stateMatch(s.abbr, "state", raw);
    if (m) scored.push({ score: score * 10, match: m });
  }

  for (const c of options.counties ?? []) {
    const score = rank(c.name);
    if (score < 0) continue;
    const stateName = STATE_ABBR_TO_NAME[c.stateAbbr];
    if (!stateName) continue;
    scored.push({
      score: score * 10 + 5, // counties sort just below the equivalent state rank
      match: {
        method: "county",
        label: `${countyLabel(c)}, ${c.stateAbbr}`,
        stateAbbr: c.stateAbbr,
        stateName,
        state: getStateByAbbr(c.stateAbbr),
        county: { fips: c.fips, name: c.name, slug: c.slug },
        query: raw,
      },
    });
  }

  // Known-city fallback: only when nothing else matched, so "Charleston" still
  // resolves even though it isn't a state or a county name.
  if (scored.length === 0) {
    const city = resolveQuery(raw);
    if (city) return [city];
  }

  return scored
    .sort((a, b) => a.score - b.score || a.match.label.localeCompare(b.match.label))
    .slice(0, limit)
    .map((x) => x.match);
}
